import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import DataTable from "../../components/common/DataTable";
import { AuthContext } from "../../context/AuthContext";
import { genericGet } from "../../services/apiServices";
import { GET_IMPORT_SHIPMENT_TRACKING } from "../../services/apiRoutes";
import CustomReport from "../common/CustomReport";
import { getColorById, getColorPlate, getPortMasters, getSuppliersList } from "../../services/CommonServices";
import { Box, Grid } from "@mui/material";
import CustomFilter from "../common/CustomFilter";
import CustomTextField from "../common/CustomTextField";
import CustomAutocomplete from "../common/CustomAutoComplete";
import CustomButton from "../common/CustomButton";
import FindReplaceOutlinedIcon from '@mui/icons-material/FindReplaceOutlined';
import { ClearRounded, } from "@mui/icons-material";
import { genericPost } from "../../services/authServices";
import CustomDataTable from "../common/CustomDataTable";
import { AccessDenied, hasCreatePermission, hasDeletePermission, hasEditPermission, hasViewPermission } from "../../services/CommonServices";
import { useLocation } from "react-router-dom";
import { useCompanyDateFormat } from "../../constants/useCompanyDateFormat";
import CustomStatus from "../common/CustomStatus";


const title = "Import Shipment Tracking";

const Function = () => {
    const { t, i18n } = useTranslation();
    const companyDateFormat = useCompanyDateFormat();
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const { token } = useContext(AuthContext)
    const [permission, setPermissions] = useState()
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const filterOpen = () => setOpenDrawer(true);
    const filterClose = () => setOpenDrawer(false);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [suppliersList, setSuppliersList] = useState([]);
    const [portMasters, setPortMasters] = useState([]);
    const [page, setPage] = useState(0); // UI 0-based
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);
    const [colorlist, setColorlist] = useState([]);

    const [filterPayload, setFilterPayload] = useState({
        shipmentId: "",
        supplierNamesId: "",
        portOfLoading: "",
        portOfDestination: "",
        fromDate: '',
        toDate: '',

    });
    const location = useLocation();
    const currentPath = location.pathname;
    useEffect(() => {
        let rolePermisionsData = JSON.parse(sessionStorage.getItem('userData')) || []
        let editPermision = hasEditPermission(currentPath, rolePermisionsData?.permissions)
        let deletePermission = hasDeletePermission(currentPath, rolePermisionsData?.permissions)
        let createPermision = hasCreatePermission(currentPath, rolePermisionsData?.permissions)
        let viewPermision = hasViewPermission(currentPath, rolePermisionsData?.permissions)

        setPermissions({
            edit: editPermision,
            delete: deletePermission,
            create: createPermision,
            view: viewPermision,
        })
    }, [])
    const handleFilterChange = (field, value) => {
        setFilterPayload((prev) => ({
            ...prev,
            [field]: value || '',
        }));
    };
    const handleClearFilters = useMemo(() => {
        return () => {
            setFilterPayload({
                shipmentId: "",
                supplierNamesId: "",
                portOfLoading: "",
                portOfDestination: "",
                fromDate: '',
                toDate: '',
            });
            fetchData(0, pageSize, '');
        };
    }, [pageSize]);
    useEffect(() => {
        getCommonData();
    }, []);

    const getCommonData = async () => {
        const suppliersListData = await getSuppliersList();
        setSuppliersList(suppliersListData);
        const getPortMastersData = await getPortMasters()
        setPortMasters(getPortMastersData);
        const colorData = await getColorPlate();
        setColorlist(colorData);
    }

    const columns = useMemo(
        () => [
            { accessorKey: "index", header: "SN.", size: 50, tableHeader: { alignItems: 'center', }, tableStyle: { textAlign: "center" }, enableColumnFilter: false },
            { accessorKey: 'date', header: 'Date', size: 50, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'shipmentId', header: 'Shipment Id', size: 100, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'proformaInvoiceNo', header: 'PI No.', size: 100, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'commercialInv', header: 'CI No.', size: 100, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'supplierName', header: 'Supplier Name', size: 150 },

            {
                accessorKey: "billingCountry",
                header: "Country",
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <CustomStatus
                            title={row.original.billingCountry}
                            sx={{
                                backgroundColor: row.original.countryIdBackground,
                                color: row.original.countryIdColor,
                                border: `1px solid ${row.original.countryIdColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },
            { accessorKey: 'amount', header: 'Amount', size: 150, tableStyle: { textAlign: "center" }, },

            {
                accessorKey: "portOfLoading",
                header: "Port Of Loading",
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <CustomStatus
                            title={row.original.portOfLoading}
                            sx={{
                                backgroundColor: row.original.portOfLoadingBackground,
                                color: row.original.portOfLoadingColor,
                                border: `1px solid ${row.original.portOfLoadingColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },
            {
                accessorKey: "portOfDestination",
                header: "Port Of Destination",
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <CustomStatus
                            title={row.original.portOfDestination}
                            sx={{
                                backgroundColor: row.original.portOfDestinationBackground,
                                color: row.original.portOfDestinationColor,
                                border: `1px solid ${row.original.portOfDestinationColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },
            // { accessorKey: 'shippingStatus', header: 'Shipment Status', size: 150 },
            {
                accessorKey: 'shippingStatus', header: 'Shipment Status',
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <CustomStatus
                            title={row.original.shippingStatus}
                            sx={{
                                backgroundColor: row.original.shippingStatusBackground,
                                color: row.original.shippingStatusColor,
                                border: `1px solid ${row.original.shippingStatusColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },
        ],
        [permission]
    );
    const formatPortName = (port, country) => {
        return [port, country].filter(Boolean).join(", ");
    };

    const fetchData = async (pageIndex = 0, pageSize = 10) => {
        setLoading(true);
        try {
            const payload = {
                page: pageIndex + 1,
                limit: pageSize,
                ...filterPayload
            };

            const response = await genericPost(GET_IMPORT_SHIPMENT_TRACKING, payload, {});
            if (response.data.success && response.data.commercialInvExps) {
                const mappedData = response.data.commercialInvExps.map((item, index) => {
                    const totalAmount = item.particulars?.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || 0;

                    const countryColorObj = getColorById(item?.clientCompany?.billingCountry?.id, colorlist, {
                        matchKey: "colorId",
                        prefixLength: 1,
                    });
                    const shippingStatusColorObj = getColorById(item?.shippingTracker?.shippingStatus?.shippingStatusId, colorlist, {
                        matchKey: "colorId",
                        prefixLength: 1,
                    });

                    return {
                        index: (index + 1).toString().padStart(2, '0'),
                        shipmentId: item.shipmentId || '',
                        proformaInvoiceNo: item?.ProformaInvoice?.proformaInvoiceNo || '',
                        commercialInv: item.commercialInvoiceNo || '',
                        supplierName: item?.clientCompany?.clientCompanyName || '',
                        billingCountry: item?.clientCompany?.billingCountry?.name || '',
                        amount: `${item?.currency?.currency_symbol || ''}${totalAmount.toLocaleString()}`,
                        shippingStatus: item?.shippingTracker?.shippingStatus?.title || '',
                        date: companyDateFormat(item?.date) || '',
                        portOfLoading: formatPortName(
                            item?.loading?.portName,
                            item?.loading?.country?.name
                        ),
                        portOfDestination: formatPortName(
                            item?.destination?.portName,
                            item?.destination?.country?.name
                        ),
                        countryIdColor: countryColorObj.color || '',
                        countryIdBackground: countryColorObj.backgroundColor || '',
                        portOfLoadingColor: item?.loading?.ColorPlate?.color || '',
                        portOfLoadingBackground: item?.loading?.ColorPlate?.backgroundColor || '',
                        portOfDestinationColor: item?.destination?.ColorPlate?.color || '',
                        portOfDestinationBackground: item?.destination?.ColorPlate?.backgroundColor || '',
                        shippingStatusColor: shippingStatusColorObj.color || '',
                        shippingStatusBackground: shippingStatusColorObj.backgroundColor || '',
                    };
                });
                setData(mappedData);
                setRowCount(response.data.pagination?.totalRecords || 0);
            } else {
                setError('No commercial invoice records found');
            }
        } catch (error) {
            setError('Error fetching commercial invoice data');
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(page, pageSize)
    }, [token, page, pageSize, colorlist])

    const defaultView = 'importshipmenttracking';
    const mainContent = (
        <>
            <CustomDataTable
                title={title}
                columns={columns}
                data={data}
                handleFilterButton={filterOpen}
                fiterButtonTitile="Fiter"
                rowCount={rowCount}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                fromDate={filterPayload.fromDate || ""}
                toDate={filterPayload.toDate || ""}
            />
            <CustomFilter
                drawerTitle="Filter Options"
                drawerWidth={300}
                openDrawer={openDrawer}
                onClose={filterClose}
                drawerContent={
                    <>
                        <Box>
                            <Grid container spacing={1} sx={{ padding: 0, }}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        type="text"
                                        placeholder="Enter Shipment ID"
                                        value={filterPayload.shipmentId}
                                        onChange={(e) => handleFilterChange('shipmentId', e.target.value)}

                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomAutocomplete
                                        options={suppliersList}
                                        getOptionLabel={(option) => option.title}
                                        placeholder="Select Supplier"
                                        onChange={(event, newValue) => handleFilterChange("supplierNamesId", newValue?.id)}
                                        value={suppliersList.find((item) => item.id === filterPayload.supplierNamesId) || null}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomAutocomplete
                                        options={portMasters}
                                        getOptionLabel={(option) => option.title}
                                        placeholder="Select Port Of Loading"
                                        onChange={(event, newValue) => handleFilterChange("portOfLoading", newValue?.id)}
                                        value={portMasters.find((item) => item.id === filterPayload.portOfLoading) || null}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomAutocomplete
                                        options={portMasters}
                                        getOptionLabel={(option) => option.title}
                                        placeholder="Select Port Of Destination"
                                        onChange={(event, newValue) => handleFilterChange("portOfDestination", newValue?.id)}
                                        value={portMasters.find((item) => item.id === filterPayload.portOfDestination) || null}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="from Date"
                                        type="date"
                                        value={filterPayload.fromDate}
                                        onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                        placeholder="From Date"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="to Date"
                                        type="date"
                                        value={filterPayload.toDate}
                                        onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                        placeholder="To Date"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12} sx={{ display: 'flex', alignItems: 'center', marginTop: "10px" }}>
                                    <CustomButton
                                        startIcon={<FindReplaceOutlinedIcon />}
                                        title="Apply"
                                        size="small"
                                        variant="contained"
                                        onClick={fetchData}
                                    />
                                    <CustomButton
                                        variant="outlined"
                                        title="Clear"
                                        startIcon={<ClearRounded />}
                                        onClick={handleClearFilters}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </>
                }
            />
        </>
    );

    const getMainContent = (mainContent, permission) => {
        return permission?.view ? mainContent : null;
    };


    return (
        <>
            <CustomReport
                defaultView={defaultView}
                mainContent={getMainContent(mainContent, permission)} />
            {!permission?.view && <AccessDenied />}

        </>

    );
};
export default Function;
